/**
 * @fileoverview Formatting for cost breakdowns — the two decisions every
 * breakdown in the app was making for itself.
 *
 * Presentational only, and deliberately currency-free: the glyph is a prop that
 * arrives from the regime profile, never a constant here
 * (docs/features/uk-cbam-cost-breakdown/00-OVERVIEW.md D8, I4).
 *
 * Both helpers render an em-less '-' for an absent value rather than an empty
 * string: a breakdown row with nothing in it reads as a broken layout, and the
 * dash is what every existing consumer already showed.
 */
import { formatDecimal, formatCurrency } from 'helpers/numberFormat';

/**
 * A quantity, at a fixed number of decimals, in the viewer's decimal
 * convention.
 *
 * @param {number|string|null|undefined} value
 * @param {number} [decimals=3]
 * @param {Object} [options] - passed through to `formatDecimal`
 * @returns {string}
 */
export const formatBreakdownNumber = (value, decimals = 3, options) =>
  value === null || value === undefined ? '-' : formatDecimal(value, decimals, options);

/**
 * An amount of money. `currency` is a caller's business — omitting it defers to
 * the shared helper's own default rather than establishing a second one here.
 *
 * @param {number|string|null|undefined} value
 * @param {Object} [options]
 * @param {string} [options.currency] - ISO 4217 code
 * @param {number} [options.decimals=2]
 * @returns {string}
 */
export const formatBreakdownCurrency = (value, { currency, decimals = 2, ...rest } = {}) => {
  if (value === null || value === undefined) return '-';
  return formatCurrency(value, {
    ...(currency ? { currency } : {}),
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
    ...rest,
  });
};
